import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getDefaultGame, getGameById } from "../api/game";
import { getGroupById, postGroup } from "../api/group";
import flechaAvanzar from "../assets/right-arrow.svg";
import addIcon from "../assets/register/add-icon.svg";
import icofoto from "../assets/register/ico-foto.svg";
import removeIcon from "../assets/register/remove-icon.svg";
import { setGame, setGroup } from "../redux/actions";
import { ROUTES } from "../utils/constants";
import {
	getGroupId,
	getTimer,
	setGroupId,
	setGroupTable,
	setLastTickNow,
	updateTimer,
} from "../utils/helpers";
import { alertError } from "../utils/logger";
import "./Register.css";

function getEmptyGroup() {
	return {
		fullName: "",
		game: null,
		image: "",
		tableNum: "",
		members: [],
	};
}

function Register() {
	const [activeGroup, setActiveGroup] = useState(getEmptyGroup());
	const [actualMember, setActualMember] = useState("");
	const [members, setMembers] = useState([]);
	const [selectedImage, setSelectedImage] = useState(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const prevState = useLocation();

	const noTimeScreens = [
		ROUTES.START_GAME,
		ROUTES.START_GAME2,
		ROUTES.START_GAME3,
		ROUTES.START_GAME4,
		ROUTES.START_GAME5,
		ROUTES.END,
		ROUTES.RESULTS,
		ROUTES.GAMEOVER,
	];

	useEffect(() => {
		window.scrollTo(0, 0);
		const asyncFetch = async () => {
			if (prevState?.state?.game) {
				getGameById(prevState.state.game).then((res) => {
					if (res.status === 200) {
						handleData(res.data);
					}
				});
			} else {
				getDefaultGame().then((res) => {
					if (res.status === 200) {
						const defaultGame = res.data;
						handleData(defaultGame);
					}
				});
			}
		};

		asyncFetch();
	}, []);

	useEffect(() => {
		if (!selectedImage) {
			setImagePreviewUrl(null);
			return undefined;
		}
		const url = URL.createObjectURL(selectedImage);
		setImagePreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [selectedImage]);

	function handleData(game) {
		dispatch(setGame(game));
		setActiveGroup((prevGroup) => ({
			...prevGroup,
			game: game._id,
		}));
		const groupId = getGroupId();
		if (groupId) {
			getGroupById(groupId)
				.then((res) => {
					if (res.status === 200 && res.data && String(res.data.game) === String(game._id)) {
						reconnect(res.data);
					}
				})
				.catch(() => {});
		}
	}

	function saveGroup(e) {
		e.preventDefault();
		if (loading) return;
		if (!members.length) {
			alertError({
				error: " ",
				customMessage: "¡No has añadido aún ningún participante!",
				appearance: "register",
			});
			return;
		}
		if (!String(activeGroup.fullName || "").trim()) {
			alertError({
				error: " ",
				customMessage: "¡El nombre del grupo es necesario!",
				appearance: "register",
			});
			return;
		}
		setLoading(true);
		const payload = { ...activeGroup, members };
		postGroup(payload, selectedImage)
			.then((res) => {
				const group = res.data;
				if (res.status === 201) {
					group.time = 0;
					setGroupId(group._id);
					setGroupTable(group.tableNum);
					dispatch(setGroup(group));
					navigate(ROUTES.START_GAME);
				} else {
					setLoading(false);
				}
			})
			.catch((error) => {
				const serverMsg =
					error?.response?.data?.message ||
					error?.response?.data?.error ||
					error?.message;
				alertError({
					error,
					customMessage:
						serverMsg ||
						"No se pudo completar el registro (revisad red y tamaño de la foto).",
					appearance: "register",
				});
				setLoading(false);
			});
	}

	function reconnect(group) {
		setGroupId(group._id);
		setGroupTable(group.tableNum);
		dispatch(setGroup(group));
		if (group.lastVisited) {
			if (!noTimeScreens.includes(group.lastVisited)) {
				const t = parseInt(group?.time ?? 0, 10);
				const hasTime = Number.isFinite(t);
				if (getTimer() === null && hasTime) {
					updateTimer(t);
					setLastTickNow();
				} else if (!group.finished) {
					updateTimer(t);
				}
			}
			navigate(group.lastVisited);
		} else navigate(ROUTES.START_GAME);
	}

	const handleAddMember = () => {
		if (
			members.length < 9 &&
			actualMember.length !== 0 &&
			actualMember.length < 45 &&
			actualMember.trim().indexOf(" ") !== -1
		) {
			setMembers([...members, actualMember]);
			setActualMember("");
		} else {
			const message =
				actualMember.length >= 45
					? "Nombre demasiado largo"
					: actualMember.trim().indexOf(" ") === -1
						? "¡Cada participante debe tener nombre y apellido!"
						: "Los grupos deben ser de máximo 9 participantes";
			alertError({
				error: " ",
				customMessage: message,
				appearance: "register",
			});
		}
	};

	const handleChange = (element) => (event) => {
		setActiveGroup({ ...activeGroup, [element]: event.target.value });
	};

	return (
		<div className="register-page">
			<div className="register-inner">
				<header className="register-header">
					<span className="register-badge">
						<span className="register-badge-dot" aria-hidden />
						Corporación Financiera Cobega
					</span>
					<h1 className="register-title">
						Registro del{" "}
						<span className="register-title-accent">equipo</span>
					</h1>
					<p className="register-lead">
						Añadid la foto del grupo, los integrantes y el nombre del
						equipo.
					</p>
				</header>

				<form className="register-form" onSubmit={saveGroup} noValidate>
					<div className="register-grid">
						<section className="register-panel" aria-labelledby="register-photo-title">
							<h2 id="register-photo-title" className="register-section-title">
								Foto de equipo
							</h2>
							<p className="register-section-hint">
								Tocad el recuadro para elegir una imagen (opcional pero
								recomendable).
							</p>
							<label className="register-photo-label" htmlFor="register-photo-input">
								<div className="register-photo-preview">
									<img
										src={imagePreviewUrl || icofoto}
										alt=""
										className={
											imagePreviewUrl ? "" : "register-photo-placeholder"
										}
									/>
								</div>
								<input
									id="register-photo-input"
									className="register-sr-only"
									name="register-photo"
									type="file"
									accept="image/*"
									capture="user"
									onChange={(e) => {
										const file = e.target.files?.[0];
										if (file) setSelectedImage(file);
									}}
								/>
							</label>
						</section>

						<section className="register-panel" aria-labelledby="register-members-title">
							<h2 id="register-members-title" className="register-section-title">
								Participantes
							</h2>
							<p className="register-section-hint">
								Nombre y apellido. Máximo 9 personas por grupo.
							</p>
							<div className="register-add-row">
								<input
									id="register-member-name"
									type="text"
									value={actualMember}
									onChange={(event) => setActualMember(event.target.value)}
									autoComplete="off"
									placeholder="Nombre y apellido"
									maxLength={44}
									className="register-input"
								/>
								<button
									type="button"
									className="register-btn-icon"
									onClick={handleAddMember}
									aria-label="Añadir participante"
								>
									<img src={addIcon} alt="" />
								</button>
							</div>
							<div className="register-members-block">
								<h3 className="register-members-heading">
									Lista del grupo
								</h3>
								{members.length > 0 ? (
									<ul className="register-members-list">
										{members.map((name, index) => (
											<li key={`${name}-${index}`} className="register-member-row">
												<span className="register-member-name">{name}</span>
												<button
													type="button"
													className="register-member-remove"
													onClick={() =>
														setMembers(
															members.filter((_, i) => i !== index)
														)
													}
													aria-label={`Quitar a ${name}`}
												>
													<img src={removeIcon} alt="" />
												</button>
											</li>
										))}
									</ul>
								) : (
									<p className="register-empty-hint">
										Debes añadir al menos un participante para continuar.
									</p>
								)}
							</div>
						</section>
					</div>

					<section className="register-panel" aria-labelledby="register-team-title">
						<h2 id="register-team-title" className="register-section-title">
							Nombre del equipo
						</h2>
						<p className="register-section-hint">Máximo 15 caracteres.</p>
						<input
							id="register-team-name"
							type="text"
							autoComplete="off"
							maxLength={15}
							onChange={handleChange("fullName")}
							value={activeGroup.fullName}
							placeholder="Nombre del equipo"
							required
							className="register-input"
							style={{ width: "100%", maxWidth: "min(100%, 420px)" }}
						/>
					</section>

					<div className="register-footer">
						<button
							type="submit"
							className="register-submit"
							disabled={loading}
							aria-busy={loading}
						>
							{loading ? (
								<span className="register-submit-spinner" aria-hidden />
							) : (
								<img
									className="register-submit-icon"
									src={flechaAvanzar}
									alt=""
								/>
							)}
							{loading ? "Creando…" : "Siguiente"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default Register;
